<script setup lang="ts">
import Spinner from '@/components/BaseSpinner.vue'
import { toast } from '@/composables/baseToast'
import { checkSessionIsValid, supabase } from '@/composables/supabaseClient'
import toastThrowError from '@/helpers/toastThrowError'
import router from '@/router'
import { ref } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const routerRedirectUrl = route.query.redirectUrl
// BUG - Supabase cannot connect to the server when calling auth.signInWithPassword when the url
// doesn't have a `?` before the `#` and will fail with `AuthRetryableFetchError` then cause the
// window to reload with the correct url. This code provides a workaround that replaces the current
// requested url with the one that the signInWithPassword function succeeds with.
const requiredUrl = `${import.meta.env.BASE_URL}?#/login?redirectUrl=${routerRedirectUrl}`
if (window.location.href !== requiredUrl) {
  window.location.replace(requiredUrl)
}

const loading = ref(false)
const email = ref<string>('')
const password = ref<string>('')

async function loginWithPassword() {
  if (!email.value || !password.value) {
    toast('Missing email or password', { type: 'error' })
  }
  try {
    loading.value = true
    const { error } = await supabase.auth.signInWithPassword({
      email: email.value,
      password: password.value,
    })
    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        toast(error.message, { type: 'error' })
        return
      }
      throw toastThrowError(error.message, error)
    }
    const validSession = await checkSessionIsValid()
    if (validSession) {
      await router.push(routerRedirectUrl ? routerRedirectUrl.toString() : '/')
    } else {
      toast('Unable to login', { type: 'error' })
    }
  } catch (err) {
    console.log('Caught error', err)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="flex h-full w-full">
    <div class="hidden w-full md:inline-block">
      <img
        class="h-full"
        src="https://raw.githubusercontent.com/prebuiltui/prebuiltui/main/assets/login/leftSideImage.png"
        alt="leftSideImage"
      />
    </div>

    <div class="flex w-full flex-col items-center justify-center">
      <form class="flex w-80 flex-col items-center justify-center md:w-96">
        <h2 class="text-4xl font-medium text-gray-900">Sign in</h2>
        <p class="mt-3 mb-5 text-sm text-gray-500/90">Welcome back! Please sign in to continue</p>

        <div
          class="flex h-12 w-full items-center gap-2 overflow-hidden rounded-full border border-gray-300/60 bg-transparent pl-6"
        >
          <svg
            width="16"
            height="11"
            viewBox="0 0 16 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M0 .55.571 0H15.43l.57.55v9.9l-.571.55H.57L0 10.45zm1.143 1.138V9.9h13.714V1.69l-6.503 4.8h-.697zM13.749 1.1H2.25L8 5.356z"
              fill="#6B7280"
            />
          </svg>
          <input
            v-model="email"
            type="email"
            placeholder="Email id"
            class="h-full w-full bg-transparent text-sm text-gray-500/80 placeholder-gray-500/80 outline-none"
            required
          />
        </div>

        <div
          class="mt-6 flex h-12 w-full items-center gap-2 overflow-hidden rounded-full border border-gray-300/60 bg-transparent pl-6"
        >
          <svg
            width="13"
            height="17"
            viewBox="0 0 13 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 8.5c0-.938-.729-1.7-1.625-1.7h-.812V4.25C10.563 1.907 8.74 0 6.5 0S2.438 1.907 2.438 4.25V6.8h-.813C.729 6.8 0 7.562 0 8.5v6.8c0 .938.729 1.7 1.625 1.7h9.75c.896 0 1.625-.762 1.625-1.7zM4.063 4.25c0-1.406 1.093-2.55 2.437-2.55s2.438 1.144 2.438 2.55V6.8H4.061z"
              fill="#6B7280"
            />
          </svg>
          <input
            v-model="password"
            type="password"
            placeholder="Password"
            class="h-full w-full bg-transparent text-sm text-gray-500/80 placeholder-gray-500/80 outline-none"
            required
          />
        </div>

        <div class="mt-8 flex w-full items-center justify-between text-gray-500/80">
          <div class="flex items-center gap-2">
            <input class="h-5" type="checkbox" id="checkbox" />
            <label class="text-sm" for="checkbox">Remember me</label>
          </div>
          <a class="text-sm underline" href="#">Forgot password?</a>
        </div>

        <button
          @click="loginWithPassword()"
          class="mt-8 h-11 w-full rounded-full bg-indigo-500 text-white transition-opacity hover:opacity-90 disabled:bg-indigo-300"
          :disabled="!email || !password"
        >
          <Spinner v-if="loading"></Spinner>
          <div v-else>Login</div>
        </button>
        <p class="mt-4 text-sm text-gray-500/90">
          Don’t have an account? <a class="text-indigo-400 hover:underline" href="#">Sign up</a>
        </p>
      </form>
    </div>
  </div>
</template>
